import { Component, EventEmitter, inject, OnInit, Output, signal } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { IntegrationsApiService } from '@shared/services/integrations-api.service';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { InputComponent } from '@shared/controls/input/input.component';
import { CommonModalFormComponent } from '@shared/components/commons/common-modal-form/common-modal-form.component';
import { AnnatureRegisterAccount } from '../../../../../../models/annature/annature-register-account';
import { ConnectionStatusEnum } from '@models/integrations/connection-status.enum';
import { ConnectionResponse } from '@models/integrations/integration-connection-response';
import { OrganisationApiService } from "@shared/services/organisation-api.service";

@Component({
  selector: 'app-annature-register-modal',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    InputComponent,
    CommonModalFormComponent,
  ],
  templateUrl: 'annature-register-modal.component.html',
  styleUrl: 'annature-register-modal.component.scss'
})
export class AnnatureRegisterModalComponent implements OnInit {
  private activeModal = inject(NgbActiveModal);
  private integrationsService = inject(IntegrationsApiService);
  private toastr = inject(ToastrService);
  private organisationService = inject(OrganisationApiService);

  isLoading = signal(false);
  form = new FormGroup({
    name: new FormControl<string | null>(null, [Validators.required]),
    email: new FormControl<string | null>(null, [Validators.required, Validators.email]),
    business: new FormControl<string | null>(null, [Validators.required]),
    number: new FormControl<string | null>(null, []),
  });

  ngOnInit(): void {
    const profile = this.organisationService.getCachedProfile();
    const fullName = [profile?.firstName, profile?.middleName, profile?.lastName].filter(Boolean).join(' ');
    this.form.patchValue({
      name: fullName,
      email: profile?.email,
      business: profile?.organisationName,
      number: profile?.phoneNumber,
    });
  }

  @Output() confirmEvent = new EventEmitter<ConnectionStatusEnum>();

  confirm(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const payload = new AnnatureRegisterAccount(this.form.value as Partial<AnnatureRegisterAccount>);

    this.isLoading.set(true);
    this.form.disable();

    this.integrationsService.registerAnnatureAccount(payload)
      .subscribe({
        next: (response) => {
          this.showResponseToaster(response);
          this.confirmEvent.emit(response.status);
          this.activeModal.close();
        },
        error: () => {
          this.form.enable();
          this.isLoading.set(false);
          this.toastr.success('Registration Failed!', 'Error');
        }
      });
  }

  close(): void {
    this.activeModal.dismiss();
  }

  showResponseToaster(response: ConnectionResponse): void {
    switch (response.status) {
      case ConnectionStatusEnum.Connected:
      case ConnectionStatusEnum.WaitForVerification:
        this.toastr.success('Registration Successful!', 'Success');
        break;
      case ConnectionStatusEnum.ConnectionFailed:
      case ConnectionStatusEnum.NotConnected:
        this.toastr.error(response.message ? response.message : 'Registration Failed!', 'Error');
        break;
    }
  }

}
